import { apiClient } from './api';
import {
  DocumentAuditHistoryResponse,
  DocumentAuditHistoryRequest,
  DocumentAuditExportRequest,
  DocumentAuditExportResponse
} from '../types/audit';

class AuditService {
  private readonly endpoints = {
    history: '/admin/documents/audit/history',
    export: '/admin/documents/audit/export',
    download: (filename: string) => `/admin/documents/audit/export/download/${encodeURIComponent(filename)}`
  } as const;

  /**
   * Get audit history for a specific document
   */
  async getDocumentAuditHistory(
    documentId: string,
    params?: Omit<DocumentAuditHistoryRequest, 'document_id'>
  ): Promise<DocumentAuditHistoryResponse> {
    if (!documentId || documentId.trim() === '') {
      throw new Error('Document ID is required');
    }

    try {
      const queryParams = new URLSearchParams();
      
      if (params?.document_type) {
        queryParams.append('document_type', params.document_type);
      }
      if (params?.limit !== undefined) {
        queryParams.append('limit', String(params.limit));
      }
      if (params?.offset !== undefined) {
        queryParams.append('offset', String(params.offset));
      }
      
      const url = `/admin/document-audit/documents/${documentId}/history${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch audit history:', error);
      throw new Error('Unable to retrieve audit history. Please try again.');
    }
  }

  /**
   * Get audit history with filters
   */
  async getAuditHistory(params: DocumentAuditHistoryRequest): Promise<DocumentAuditHistoryResponse> {
    try {
      const queryParams = new URLSearchParams(this.objectToQueryParams(params as Record<string, unknown>));
      const response = await apiClient.get(`${this.endpoints.history}?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch audit history:', error);
      throw new Error('Unable to retrieve audit history. Please try again.');
    }
  }

  /**
   * Export audit history
   */
  async exportAuditHistory(params: DocumentAuditExportRequest): Promise<DocumentAuditExportResponse> {
    try {
      const response = await apiClient.post(this.endpoints.export, params);
      return response.data;
    } catch (error) {
      console.error('Failed to export audit history:', error);
      throw new Error('Unable to export audit history. Please try again.');
    }
  }

  /**
   * Download audit export file
   */
  async downloadAuditExport(filename: string): Promise<Blob> {
    if (!filename || filename.trim() === '') {
      throw new Error('Filename is required');
    }

    // Validate filename to prevent path traversal
    if (filename.includes('..') || filename.includes('/') || filename.includes('\\')) {
      throw new Error('Invalid filename');
    }

    try {
      const response = await apiClient.get(this.endpoints.download(filename), {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Failed to download audit export:', error);
      throw new Error('Unable to download audit export. Please try again.');
    }
  }

  /**
   * Download audit export and trigger browser download
   */
  async downloadAndSaveAuditExport(filename: string): Promise<void> {
    const blob = await this.downloadAuditExport(filename);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private objectToQueryParams(obj: Record<string, unknown>): Record<string, string> {
    const params: Record<string, string> = {};
    
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle arrays for multi-value parameters
        if (Array.isArray(value)) {
          params[key] = value.join(',');
        } else {
          params[key] = String(value);
        }
      }
    });
    
    return params;
  }
}

export const auditService = new AuditService();